import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight, Download } from "lucide-react";
import { fetchCatalogStatus, uploadUrl } from "@/lib/api";

/**
 * Public landing page at /lookbook (also /catalogue). No login required.
 * Shows a branded page with a big Download button that opens the current
 * prebook lookbook PDF, plus a call-to-action to apply for wholesale
 * access. Meant to be shareable via a single link (Instagram bio, DMs,
 * emails to prospective buyers, etc.).
 */
export default function Lookbook() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchCatalogStatus();
        setStatus(s || {});
      } catch (e) {
        setStatus({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pdf = status?.prebook?.url ? uploadUrl(status.prebook.url) : null;

  return (
    <div className="page-card" data-testid="lookbook-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-14 md:py-24 text-center">
        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-4">Wholesale</p>
        <h1
          className="font-serif text-4xl md:text-6xl mb-4 text-primary"
          style={{ fontFamily: '"Snowyn Snowman", "Caveat Brush", cursive' }}
        >
          Drop 1 Lookbook
        </h1>
        <p className="text-base text-muted-foreground max-w-md mx-auto mb-10">
          Every style, size, and wholesale price in one PDF. Have a look, then apply for a
          wholesale account to place your order.
        </p>

        {loading ? (
          <div className="h-14 w-64 mx-auto rounded bg-secondary animate-pulse" />
        ) : pdf ? (
          <a
            href={pdf}
            target="_blank"
            rel="noreferrer"
            data-testid="lookbook-download-btn"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
          >
            <Download size={18} strokeWidth={1.75} />
            Download the lookbook (PDF)
          </a>
        ) : (
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-border bg-secondary text-sm text-muted-foreground">
            <FileText size={16} strokeWidth={1.5} />
            The lookbook is being finalized. Check back soon.
          </div>
        )}

        <div className="mt-16 pt-10 border-t border-border">
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Ready to order?</p>
          <h2 className="font-serif text-2xl md:text-3xl mb-3">Apply for wholesale access</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            Approval usually takes 1–2 business days. Once you're approved you'll unlock
            wholesale pricing and can place prebook orders directly through the site.
          </p>
          <Link
            to="/register"
            data-testid="lookbook-apply-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded bg-white border border-primary text-primary font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Apply now <ArrowRight size={15} strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </div>
  );
}
